
import React from 'react';
import {
  Box,
  TextField,
  FormControlLabel,
  Switch,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Chip,
  InputAdornment
} from '@mui/material';
import { CalendarMonthOutlined, AccessTimeOutlined, NumbersOutlined } from '@mui/icons-material';
import { inputSx, selectSx, statusChipSx, tintSx, FONT } from '../../styles/wizardSx';

/**
 * Date / datetime picker with optional relative offsets.
 *
 * Absolute: ISO date / datetime-local string.
 * Relative: { mode: 'relative', amount, unit, direction: 'after'|'before', anchor }
 *   serialized by transformService to RELATIVE::{signedAmount}::{unit}[::{anchor}]
 *
 * @param {Array<{id: string, label: string}>} anchors - optional field anchors (e.g. departure_date)
 * @param {string} minAbsolute - optional min value for absolute datetime-local / date inputs
 */
function DateField({ field, value, onChange, anchors = [], minAbsolute, error, helperText, ...props }) {
  const isRelative = typeof value === 'object' && value?.mode === 'relative';
  const showAnchors = Array.isArray(anchors) && anchors.length > 0;

  const handleToggle = (checked) => {
    if (checked) {
      onChange({
        mode: 'relative',
        amount: 1,
        unit: 'days',
        direction: 'after',
        anchor: anchors[0]?.id || 'trigger',
      });
    } else {
      onChange('');
    }
  };

  const handleRelativeChange = (key, val) => {
    onChange({
      ...value,
      [key]: key === 'amount' ? Math.max(0, parseInt(val, 10) || 0) : val,
    });
  };

  const relativeHint = () => {
    if (!isRelative) return 'Specific date value';
    const amt = value.amount || 0;
    const unit = value.unit || 'days';
    const dir = value.direction === 'before' ? 'before' : 'after';
    const anchorLabel =
      anchors.find((a) => a.id === (value.anchor || 'trigger'))?.label ||
      'when the automation runs';
    return `${amt} ${unit} ${dir} ${anchorLabel.toLowerCase()}`;
  };

  return (
    <Box>
      <Paper 
        elevation={0}
        sx={[
          (theme) => ({
            p: 1.5,
            borderRadius: '10px',
            border: '1px solid',
            borderColor: error
              ? 'error.main'
              : isRelative
                ? 'primary.main'
                : theme.palette.outline.variant,
            bgcolor: 'background.paper'
          }),
          (theme) => (isRelative ? tintSx(theme, 0.03) : {})
        ]}
      >
        <FormControlLabel
          control={
            <Switch
              checked={isRelative}
              onChange={(e) => handleToggle(e.target.checked)}
              color="info"
            />
          }
          label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeOutlined sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Use relative date
              </Typography>
            </Box>
          }
        />
        
        {isRelative ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <TextField
                label="Amount"
                type="number"
                value={value.amount ?? 0}
                onChange={(e) => handleRelativeChange('amount', e.target.value)}
                sx={[inputSx, { width: { xs: '100%', sm: '30%' }, minWidth: 100 }]}
                inputProps={{ min: 0 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <NumbersOutlined sx={{ color: 'primary.main', fontSize: 16 }} />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl sx={{ width: { xs: '100%', sm: '30%' }, minWidth: 120 }}>
                <InputLabel>Unit</InputLabel>
                <Select
                  label="Unit"
                  value={value.unit || 'days'}
                  onChange={(e) => handleRelativeChange('unit', e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="days">Days</MenuItem>
                  <MenuItem value="weeks">Weeks</MenuItem>
                  <MenuItem value="months">Months</MenuItem>
                  <MenuItem value="years">Years</MenuItem>
                </Select>
              </FormControl>
              <FormControl sx={{ width: { xs: '100%', sm: '30%' }, minWidth: 120 }}>
                <InputLabel>Direction</InputLabel>
                <Select
                  label="Direction"
                  value={value.direction === 'before' ? 'before' : 'after'}
                  onChange={(e) => handleRelativeChange('direction', e.target.value)}
                  sx={selectSx}
                >
                  <MenuItem value="after">After</MenuItem>
                  <MenuItem value="before">Before</MenuItem>
                </Select>
              </FormControl>
            </Box>
            {showAnchors && (
              <FormControl fullWidth>
                <InputLabel>Relative to</InputLabel>
                <Select
                  label="Relative to"
                  value={value.anchor || anchors[0]?.id || 'trigger'}
                  onChange={(e) => handleRelativeChange('anchor', e.target.value)}
                  sx={selectSx}
                >
                  {anchors.map((a) => (
                    <MenuItem key={a.id} value={a.id}>
                      {a.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          </Box>
        ) : (
          <TextField
            fullWidth
            type={field.type === 'datetime' ? 'datetime-local' : 'date'}
            label={field.label}
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={[inputSx, { mt: 2 }]}
            inputProps={minAbsolute ? { min: minAbsolute } : undefined}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CalendarMonthOutlined sx={{ color: 'primary.main', fontSize: 18 }} />
                </InputAdornment>
              ),
            }}
            {...props}
          />
        )}
        
        <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Chip
            label={isRelative ? 'Dynamic' : 'Fixed'}
            size="small"
            sx={[{ height: '20px' }, statusChipSx(isRelative ? 'info' : 'neutral')]}
          />
          <Typography sx={{ fontSize: FONT.caption, color: 'text.secondary' }}>
            {relativeHint()}
          </Typography>
        </Box>
        {helperText ? (
          <Typography
            variant="caption"
            color={error ? 'error' : 'text.secondary'}
            sx={{ mt: 1, display: 'block', fontSize: FONT.caption }}
          >
            {helperText}
          </Typography>
        ) : null}
      </Paper>
    </Box>
  );
}

export default DateField;
